import stadiumBackdropUrl from '../assets/stadium-backdrop.svg';
import attackerRunAUrl from '../assets/attacker-run-a.png';
import attackerRunBUrl from '../assets/attacker-run-b.png';
import attackerShootUrl from '../assets/attacker-shoot.png';
import keeperSetUrl from '../assets/keeper-set.png';
import keeperDiveLeftUrl from '../assets/keeper-dive-left.png';

export type SceneAssetKey =
  | 'stadiumBackdrop'
  | 'attackerRunA'
  | 'attackerRunB'
  | 'attackerShoot'
  | 'keeperSet'
  | 'keeperDiveLeft';

const assetUrls: Record<SceneAssetKey, string> = {
  stadiumBackdrop: stadiumBackdropUrl,
  attackerRunA: attackerRunAUrl,
  attackerRunB: attackerRunBUrl,
  attackerShoot: attackerShootUrl,
  keeperSet: keeperSetUrl,
  keeperDiveLeft: keeperDiveLeftUrl,
};

const loadedAssets = new Map<SceneAssetKey, HTMLImageElement>();
const pendingAssets = new Map<SceneAssetKey, Promise<HTMLImageElement>>();

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load scene asset: ${url}`));
    image.src = url;
  });

const loadSceneAsset = (key: SceneAssetKey) => {
  if (loadedAssets.has(key)) {
    return Promise.resolve(loadedAssets.get(key)!);
  }

  if (pendingAssets.has(key)) {
    return pendingAssets.get(key)!;
  }

  const request = loadImage(assetUrls[key])
    .then((image) => {
      loadedAssets.set(key, image);
      pendingAssets.delete(key);
      return image;
    })
    .catch((error) => {
      pendingAssets.delete(key);
      throw error;
    });

  pendingAssets.set(key, request);
  return request;
};

export const preloadSceneAssets = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve([]);
  }

  return Promise.all((Object.keys(assetUrls) as SceneAssetKey[]).map((key) => loadSceneAsset(key))).catch(() => []);
};

export const getSceneAsset = (key: SceneAssetKey) => loadedAssets.get(key) ?? null;
